import { useEffect, useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import type { LotusLobbyGroupConfig } from '../constants/lotusCasinoLobbyGroups'
import { LOTUS_TOP_PROVIDERS } from '../constants/lotusCasinoTopProviders'
import {
  casinoTypesForProvider,
  fetchCasinoProviderList,
  LOTUS_CASINO_GC_MS,
  LOTUS_CASINO_STALE_MS,
  lotusCasinoKeys,
} from '../lib/lotusCasinoQueries'
import {
  filterGamesForLobbyGroup,
  getGameId,
  type CasinoGameRecord,
} from '../utils/lotusCasinoGameFilter'
import {
  preloadCasinoGameImages,
  resolveCasinoGameImageUrl,
} from '../utils/lotusCasinoImagePreload'

function providersForGroup(group: LotusLobbyGroupConfig): string[] {
  if (group.useTopProviders) {
    return LOTUS_TOP_PROVIDERS.map((p) => p.categoryId)
  }
  if (group.providerCategories?.length) {
    return group.providerCategories
  }
  return ['100hp']
}

type ProviderQuerySpec = {
  providerId: string
  casinoType: string
}

function buildProviderQueries(group: LotusLobbyGroupConfig | null): ProviderQuerySpec[] {
  if (!group) return []
  return providersForGroup(group).flatMap((providerId) =>
    casinoTypesForProvider(providerId).map((casinoType) => ({
      providerId,
      casinoType,
    })),
  )
}

export function useLotusLobbyGroupGames(
  group: LotusLobbyGroupConfig | null,
  options: { preLoginMode?: boolean; maxGames?: number } = {},
) {
  const { preLoginMode = false, maxGames } = options
  const providerQueries = useMemo(() => buildProviderQueries(group), [group])

  const queryResults = useQueries({
    queries: providerQueries.map(({ providerId, casinoType }) => ({
      queryKey: lotusCasinoKeys.providerList(providerId, casinoType, preLoginMode),
      queryFn: () => fetchCasinoProviderList(providerId, casinoType, preLoginMode),
      staleTime: LOTUS_CASINO_STALE_MS,
      gcTime: LOTUS_CASINO_GC_MS,
      placeholderData: (previous: CasinoGameRecord[] | undefined) => previous,
      enabled: Boolean(group),
    })),
  })

  const games = useMemo(() => {
    if (!group) return []

    const pool: CasinoGameRecord[] = []
    const seen = new Set<string>()

    queryResults.forEach((result, index) => {
      const list = result.data
      if (!Array.isArray(list)) return
      const providerId = providerQueries[index]?.providerId
      for (const game of list) {
        const id = getGameId(game)
        if (!id || seen.has(id)) continue
        seen.add(id)
        pool.push({ ...game, _providerCategory: providerId })
      }
    })

    return filterGamesForLobbyGroup(
      pool,
      { ...group, maxGames: maxGames ?? group.maxGames },
      new Set<string>(),
    )
  }, [group, maxGames, providerQueries, queryResults])

  const loading =
    Boolean(group) &&
    queryResults.length > 0 &&
    queryResults.every((result) => result.isLoading && !result.data)

  useEffect(() => {
    if (games.length === 0) return
    preloadCasinoGameImages(games.map(resolveCasinoGameImageUrl))
  }, [games])

  return { games, loading }
}

export function useLotusCategoryGames(
  category: string | null,
  casinoType = 'slots',
  options: { preLoginMode?: boolean } = {},
) {
  const { preLoginMode = false } = options

  const { data = [], isLoading } = useQuery<CasinoGameRecord[]>({
    queryKey: lotusCasinoKeys.providerList(category ?? '', casinoType, preLoginMode),
    queryFn: () => fetchCasinoProviderList(category!, casinoType, preLoginMode),
    enabled: Boolean(category),
    staleTime: LOTUS_CASINO_STALE_MS,
    gcTime: LOTUS_CASINO_GC_MS,
    placeholderData: (previous: CasinoGameRecord[] | undefined) => previous,
  })

  const games: CasinoGameRecord[] = data
  const loading = Boolean(category) && isLoading && games.length === 0

  useEffect(() => {
    if (games.length === 0) return
    preloadCasinoGameImages(games.map(resolveCasinoGameImageUrl))
  }, [games])

  return { games, loading }
}
